import React, { memo, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';

import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

L.Control.Attribution.prototype.options.prefix = false;

const MapWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  z-index: 0;

  .leaflet-container {
    width: 100%;
    height: 100%;
    z-index: 0 !important;
    background: #f5f5f5;
  }

  .leaflet-pane {
    z-index: 100 !important;
  }

  .leaflet-tile-pane {
    z-index: 200 !important;
  }

  .leaflet-overlay-pane {
    z-index: 400 !important;
  }

  .leaflet-shadow-pane {
    z-index: 500 !important;
  }

  .leaflet-marker-pane {
    z-index: 600 !important;
  }

  .leaflet-tooltip-pane {
    z-index: 650 !important;
  }

  .leaflet-popup-pane {
    z-index: 700 !important;
  }

  .leaflet-control-container {
    z-index: 800 !important;
  }
`;

const InvalidateMapSize = () => {
  const map = useMap();

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        map.invalidateSize();
      });
    });

    observer.observe(map.getContainer());

    requestAnimationFrame(() => {
      map.invalidateSize();
    });

    return () => observer.disconnect();
  }, [map]);

  return null;
};

const ChangeView = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(position, map.getZoom(), {
      animate: false,
    });
  }, [map, position]);

  return null;
};

const EventLocationMap = ({
  latitude,
  longitude,
  venueName = 'Event Location',
}) => {
  const position = useMemo(() => {
    const lat = Number(latitude);
    const lng = Number(longitude);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return [lat, lng];
  }, [latitude, longitude]);

  if (!position) return null;

  return (
    <MapWrapper>
      <MapContainer
        center={position}
        zoom={15}
        scrollWheelZoom={false}
        zoomControl
        dragging
        doubleClickZoom
        touchZoom
      >
        <InvalidateMapSize />

        <ChangeView position={position} />

        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        <Marker position={position}>
          <Popup>{venueName}</Popup>
        </Marker>
      </MapContainer>
    </MapWrapper>
  );
};

export default memo(EventLocationMap);